import React, { useState, useEffect, useRef } from 'react';
import SecurityTiles from './SecurityTiles';
import { formSchema, type FormData } from '../types/form';
import type { KbaQuestion } from '../types/api';
import {
  formatPhoneNumber,
  validateEmail,
  validatePhone,
} from '../utils/validation';
import { FormController, type EchelonProgressStage } from '../controllers/FormController';
import { storageController } from '../controllers/StorageController';
import {
  FormRouteManager,
  FormRouteEnum,
  getFlowType,
  shouldRedirectToStart,
  type FlowType,
  type FormRoute,
  useFormRouting,
} from '../routes/FormRoutes';
import { marketingParams } from '../utils/marketingParams';
import StartForm from './forms/StartForm';
import ApplicationReviewForm from './forms/ApplicationReviewForm';
import LoanPurposeForm from './forms/LoanPurposeForm';
import LoanAmountForm from './forms/LoanAmountForm';
import PropertyStatusForm from './forms/PropertyStatusForm';
import EmploymentStatusForm from './forms/EmploymentStatusForm';
import EmploymentFrequencyForm from './forms/EmploymentFrequencyForm';
import AnnualIncomeForm from './forms/AnnualIncomeForm';
import EducationForm from './forms/EducationForm';
import EmailForm from './forms/EmailForm';
import BirthDateForm from './forms/BirthDateForm';
import ContactInfoForm from './forms/ContactInfoForm';
import PhoneVerificationForm from './forms/PhoneVerificationForm';
import SSNVerificationForm from './forms/SSNVerificationForm';
import KbaPersonalInfoForm from './forms/KbaPersonalInfoForm';
import KbaSsnForm from './forms/KbaSsnForm';
import KbaQuestionsForm from './forms/KbaQuestionsForm';
import ManualVerificationForm from './forms/ManualVerificationForm';
import SuccessStep from './forms/SuccessStepModular';
import EmailVerificationModal from './EmailVerificationModal';
import ProgressBar from './ProgressBar';
import FormHeader from './FormHeader';
import LoadingOverlay from './LoadingOverlay';
import EchelonProgressOverlay from './EchelonProgressOverlay';
import TrustpilotWidget from './TrustpilotWidget';
import ApplicationBlocked from './forms/ApplicationBlocked';
import UnsuccessfulApplication from './forms/UnsuccessfulApplication';
import DeveloperTools from './DeveloperTools';
import { campaignVariantService } from '../services/campaignVariants';
import { formDefaultsService } from '../services/formDefaults';
import { clientConfig } from '../config/clientConfig';
import { trackFormStep } from '../utils/formTrack';
import { usePartnerDefaults } from '../hooks/usePartnerDefaults';
import { ReviewEditController, type ReviewEditableField } from '../routes/reviewEditController';
import { hasPartnerReviewData } from '../utils/partnerReview';
import { useLeadPreload, isAcceptedApplicationStatus } from '../hooks/useLeadPreload';
import { createLeadsService } from '../services/leadsFactory';
import {
  MAX_ATTEMPTS_PER_METHOD,
  isSmsExhausted,
  isProspectExhausted,
  isKbaExhausted,
  areAllMethodsExhausted,
} from '../utils/verificationAttempts';
import { shouldFallback } from '../utils/apiFallback';
import type { AlternativeMethodCallbacks } from '../types/verification';

const SKIP_FREQUENCY_STATUSES = ['not_employed', 'retired', 'other'];
const AUTO_ECHELON_DEBT_THRESHOLD = 1500000;

type ManualVerificationReason = 'sms_failure' | 'create_failure';

const ROUTES_WITHOUT_BACK = new Set<string>([
  FormRouteEnum.START,
  FormRouteEnum.VERIFY_SSN,
  FormRouteEnum.SUCCESS,
  FormRouteEnum.UNSUCCESSFUL,
  FormRouteEnum.BLOCK,
]);

export default function MicroForm() {
  const { currentPath, navigateToRoute } = useFormRouting();

  const mockDelay = 5000;
  const delay = async () => {
    await new Promise((resolve) => setTimeout(resolve, mockDelay));
  };

  // Check for BPO agent flag in URL
  const [isBpoAgent] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('bpo') === 'true';
  });

  const [flow] = useState<FlowType>(() => getFlowType());

  const [formController] = useState(() => {
    const controller = new FormController();
    // Set FormController reference in StorageController
    storageController.setFormController(controller);
    return controller;
  });

  const [partnerService] = useState(() => createLeadsService());
  const { partnerDefaults, isLoading: isPartnerLoading } = usePartnerDefaults(partnerService);
  const { leadDetails, isLoading: isLeadPreloadLoading } = useLeadPreload(partnerService);
  const reviewEditController = useRef(new ReviewEditController()).current;

  const [routeManager] = useState(() => new FormRouteManager(flow));
  const [formData, setFormData] = useState<FormData>(() => {
    const savedData = storageController.getFormData();
    const variant = campaignVariantService.getCurrentVariant();
    const defaults = formDefaultsService.getDefaults();
    
    const baseFormData: FormData = {
      email: savedData?.email || '',
      phone: savedData?.phone || '',
      birthDate: savedData?.birthDate || '',
      smsConsent: savedData?.smsConsent ?? true,
      promoSmsConsent: savedData?.promoSmsConsent || false,
      smsCode: savedData?.smsCode || '',
      ssnLast4: savedData?.ssnLast4 || '',
      loanAmount: savedData?.loanAmount,
      loanPurpose: savedData?.loanPurpose,
      propertyStatus: savedData?.propertyStatus,
      employmentFrequency: savedData?.employmentFrequency,
      employmentStatus: savedData?.employmentStatus,
      annualIncome: savedData?.annualIncome,
      educationLevel: savedData?.educationLevel,
      offerCode: savedData?.offerCode || variant.offerCode,
      firstName: savedData?.firstName || variant.firstName
    };
    
    // Apply form defaults
    return formDefaultsService.applyToFormData(baseFormData) as FormData;
  });

  const [validationErrors, setValidationErrors] = useState<{
    email: string | null;
    phone: string | null;
  }>({
    email: null,
    phone: null,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [echelonStage, setEchelonStage] = useState<EchelonProgressStage | null>(null);
  const [manualVerificationReason, setManualVerificationReason] = useState<ManualVerificationReason | null>(null);
  const [isUnqualified, setIsUnqualified] = useState(false);
  const [contactResult, setContactResult] = useState<'accepted' | 'rejected' | null>(null);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [smsAttempts, setSmsAttempts] = useState(0);
  const [prospectAttempts, setProspectAttempts] = useState(0);
  const [kbaAttempts, setKbaAttempts] = useState(0);
  const [kbaQuestions, setKbaQuestions] = useState<KbaQuestion[]>([]);
  const [kbaAttemptCount, setKbaAttemptCount] = useState(1);
  const MAX_KBA_ATTEMPTS = 3;
  const [contactCenterAvailability, setContactCenterAvailability] = useState<
    'none' | 'low' | 'high' | null
  >(null);
  const [shouldSendCall, setShouldSendCall] = useState(false);

  const showPartnerReview = Boolean(partnerDefaults) && !isPartnerLoading && hasPartnerReviewData(formData);

  const currentStep = routeManager.getCurrentStep(currentPath);
  const verifyPhoneStep = routeManager.getCurrentStep(FormRouteEnum.VERIFY_PHONE);
  const progressStep = currentStep === -1 ? verifyPhoneStep : currentStep;

  useEffect(() => {
    trackFormStep(currentPath as FormRoute);
  }, [currentPath]);
  // Initialize marketing params on first load
  useEffect(() => {
    try {
      // Initialize campaign variants first
      campaignVariantService.initializeFromUrl();
      formDefaultsService.initializeFromUrl();
      marketingParams.initializeFromUrl();
    } catch (err) {
      console.error('Failed to initialize marketing params:', err);
    }
  }, []);

  useEffect(() => {
    storageController.setFormData(formData);
  }, [formData]);

  useEffect(() => {
    if (!partnerDefaults) return;
    formDefaultsService.injectExternalDefaults(partnerDefaults);
    setFormData(prev => formDefaultsService.applyToFormData(prev) as FormData);
  }, [partnerDefaults]);

  useEffect(() => {
    const exhausted = areAllMethodsExhausted({ smsAttempts, prospectAttempts, kbaAttempts });
    const isTerminalRoute = (
      currentPath === FormRouteEnum.UNSUCCESSFUL ||
      currentPath === FormRouteEnum.SUCCESS ||
      currentPath === FormRouteEnum.BLOCK
    );
    if (exhausted && !isTerminalRoute) {
      navigateToRoute(FormRouteEnum.UNSUCCESSFUL);
    }
  }, [smsAttempts, prospectAttempts, kbaAttempts, currentPath, navigateToRoute]);

  useEffect(() => {
    if (currentPath === FormRouteEnum.KBA_PERSONAL_INFO && !formData.email) {
      navigateToRoute(FormRouteEnum.START);
    }
    if (currentPath === FormRouteEnum.KBA_SSN && !formData.firstName) {
      navigateToRoute(FormRouteEnum.KBA_PERSONAL_INFO);
    }
    if (currentPath === FormRouteEnum.KBA_QUESTIONS && kbaQuestions.length === 0) {
      navigateToRoute(FormRouteEnum.KBA_PERSONAL_INFO);
    }
    if (currentPath === FormRouteEnum.MANUAL_VERIFY && !formData.email) {
      navigateToRoute(FormRouteEnum.START);
    }
  }, [currentPath, formData.email, formData.firstName, kbaQuestions.length, navigateToRoute]);

  useEffect(() => {
    if (
      storageController.isApplicationBlocked() &&
      !routeManager.isBlockedRoute(currentPath as FormRoute)
    ) {
      navigateToRoute(FormRouteEnum.BLOCK);
    }
  }, [currentPath, navigateToRoute]);

  useEffect(() => {
    if (
      shouldRedirectToStart({
        flow,
        currentPath,
        hasStartedForm: formData.email !== '',
      })
    ) {
      navigateToRoute(FormRouteEnum.START);
    }
  }, [currentPath, flow, formData.email, navigateToRoute]);

  // Check contact center availability and track success page when reaching success step
  useEffect(() => {
    const checkAvailability = async () => {
      if (currentPath === FormRouteEnum.SUCCESS) {
        if (isBpoAgent) {
          setContactCenterAvailability('none');
          setShouldSendCall(false);
          return;
        }

        try {
          const result = await formController.checkContactCenterAvailability();
          if (result.success) {
            setContactCenterAvailability(result.availability);
            setShouldSendCall(result.shouldSendCall);
          } else {
            setContactCenterAvailability('none');
            setShouldSendCall(false);
          }
        } catch (err) {
          console.error('Failed to check contact center availability:', err);
          setContactCenterAvailability('none');
          setShouldSendCall(false);
        }
      }
    };

    checkAvailability();
  }, [currentPath, isBpoAgent]);

  // Deep-link straight to the success page when returning via a lead unique id
  // (e.g. ?useruniqueId=<guid>) whose application has already been accepted.
  // This only fires ONCE per page load: after the initial redirect, navigating
  // back/away (e.g. to /hold) or reloading while already blocked must behave
  // exactly like the regular flow, so it defers to the isApplicationBlocked
  // guard instead of forcing the user back to the success page every time.
  const [hasHandledLeadRedirect, setHasHandledLeadRedirect] = useState(false);

  useEffect(() => {
    if (!leadDetails) return;
    if (hasHandledLeadRedirect) return;
    setHasHandledLeadRedirect(true);

    if (!isAcceptedApplicationStatus(leadDetails.applicationStatus)) return;
    if (routeManager.isBlockedRoute(currentPath as FormRoute)) return;

    formController.hydrateFromLeadDetails(leadDetails);

    storageController.setApplicationData({
      timestamp: Date.now(),
      email: leadDetails.email,
      formData: {},
      status: 'success',
    });
    storageController.clearFormData();
    storageController.setApplicationSuccess();
    navigateToRoute(FormRouteEnum.SUCCESS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadDetails, hasHandledLeadRedirect]);

  // Track which success page variant is shown to customer
  const handleVariantShown = React.useCallback((variantId: string) => {
    formController.successPage(variantId).catch(() => void 0);
  }, [formController]);

  const handleApplicationSuccess = () => {
    // Store the first name from the contact response
    if (formController.getContactFirstName()) {
      storageController.setContactFirstName(
        formController.getContactFirstName()
      );
    }

    storageController.setApplicationData({
      timestamp: Date.now(),
      email: formData.email,
      formData: {
        loanAmount: formData.loanAmount,
        loanPurpose: formData.loanPurpose,
        employmentStatus: formData.employmentStatus,
        propertyStatus: formData.propertyStatus,
        educationLevel: formData.educationLevel,
      },
      status: 'success',
    });
    storageController.clearFormData();
    storageController.setApplicationSuccess();
    navigateToRoute(FormRouteEnum.SUCCESS);
  };

  const handleScheduleAppointment = async (
    selectedTime: Date
  ): Promise<boolean> => {
    try {
      const result = await formController.scheduleAppointment(selectedTime);
      return result.success;
    } catch (err) {
      console.error('Failed to schedule appointment:', err);
      return false;
    }
  };

  const handleInitiateCall = async () => {
    try {
      const result = await formController.connectCall();
      return {
        success: result.success,
        from: result.from,
        error: result.error,
      };
    } catch (err) {
      console.error('Failed to connect call:', err);
      return {
        success: false,
        error: 'Failed to connect call. Please try again.',
      };
    }
  };

  const handleApplicationOffers = async (offerId: string, storeUuid: string) => {
    storageController.setApplicationData({
      timestamp: Date.now(),
      email: formData.email,
      formData: {
        loanAmount: formData.loanAmount,
        loanPurpose: formData.loanPurpose,
        employmentStatus: formData.employmentStatus,
        propertyStatus: formData.propertyStatus,
        educationLevel: formData.educationLevel,
      },
      status: 'success',
    });
    storageController.clearFormData();
    storageController.setApplicationOffers();

    setLoadingMessage('Loading your offers...');

    await delay();

    const targetUrl = `${clientConfig.offersUrl}/?id=${storeUuid}`;
    //const targetUrl = `https://fiona.com/partner/symple-lending-loans/loans?results=${offerId}&step=results`;
    window.location.href = targetUrl;
  };

  const handleApplicationFailure = () => {
    storageController.setApplicationData({
      timestamp: Date.now(),
      email: formData.email,
      formData: {
        loanAmount: formData.loanAmount,
        loanPurpose: formData.loanPurpose,
        employmentStatus: formData.employmentStatus,
        propertyStatus: formData.propertyStatus,
        educationLevel: formData.educationLevel,
      },
      status: 'unsuccessful',
    });
    storageController.clearFormData();
    storageController.setApplicationFailed();
    navigateToRoute(FormRouteEnum.UNSUCCESSFUL);
  };

  const handleRejection = async (flowType: FlowType) => {
    const debtAmount = formController.getDebtAmount();
    const isEligibleForAutoEchelon = debtAmount > AUTO_ECHELON_DEBT_THRESHOLD;

    if (isEligibleForAutoEchelon) {
      const echelonResult = await formController.initiateAutoEchelonFlow({
        onProgress: (stage) => setEchelonStage(stage),
      });
      setEchelonStage(null);

      if (echelonResult.success && echelonResult.redirectUrl) {
        const url = new URL(echelonResult.redirectUrl);
        url.searchParams.set('embed', '1');
        window.location.href = url.toString();
        return;
      }
    }

    if (flowType === 'R1') {
      navigateToRoute(FormRouteEnum.VERIFY_SSN);
    } else if (flowType === 'N1') {
      setIsUnqualified(true);
      navigateToRoute(FormRouteEnum.LOAN_AMOUNT);
    } else {
      setContactResult('rejected');
      navigateToRoute(FormRouteEnum.LOAN_AMOUNT);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const formattedPhone = formatPhoneNumber(value);
      setFormData((prev) => ({ ...prev, [name]: formattedPhone }));
      setValidationErrors((prev) => ({
        ...prev,
        phone: validatePhone(formattedPhone),
      }));
    } else if (name === 'email') {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setValidationErrors((prev) => ({
        ...prev,
        email: validateEmail(value),
      }));
    } else if (name === 'smsConsent') {
      setFormData((prev) => ({ ...prev, [name]: value === 'true' }));
    } else if (name === 'promoSmsConsent') {
      setFormData((prev) => ({ ...prev, [name]: value === 'true' }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Update marketing params with form data
    marketingParams.updateFormData(formData);
  };

  const handleConnectBySms = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailError = validateEmail(formData.email);
    const phoneError = validatePhone(formData.phone);

    setValidationErrors({
      email: emailError,
      phone: phoneError,
    });

    if (emailError || phoneError) {
      return;
    }

    // Update marketing params with form data
    marketingParams.updateFormData(formData);

    setLoading(true);
    setLoadingMessage('Creating secure connection...');
    setError(null);

    try {
      const ipAddress = storageController.getUserIp() || '';
      const result = await formController.connectBySms(
        formData.phone,
        formData.email,
        formData.birthDate,
        formData.loanAmount,
        formData.loanPurpose,
        formData.employmentStatus,
        formData.employmentFrequency,
        formData.educationLevel,
        formData.annualIncome,
        formData.propertyStatus,
        ipAddress,
        marketingParams.getParams()
      );

      if (result.success) {
        storageController.setApplicationSmsCode();
        navigateToRoute(FormRouteEnum.VERIFY_PHONE);
      } else if (result.code === 500) {
        navigateToRoute(FormRouteEnum.KBA_PERSONAL_INFO);
      } else {
        throw new Error(result.error || 'Failed to create connection');
      }
    } catch (err) {
      setSmsAttempts((prev) => prev + 1);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleCodeVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setLoadingMessage('Verifying SMS code...');
    setError(null);

    try {
      const verifyResult = await formController.verifyCode(
        formData.smsCode || '',
        formData
      );

      if (!verifyResult.success) {
        if (verifyResult.code === 500) {
          navigateToRoute(FormRouteEnum.KBA_PERSONAL_INFO);
          return;
        }
        throw new Error(verifyResult.error);
      }

      setLoadingMessage('Checking eligibility...');
      const createResult = await formController.createContact(formData);

      if (!createResult.success) {
        if (createResult.status === 'ERROR' && shouldFallback(createResult.code)) {
          setManualVerificationReason('create_failure');
          navigateToRoute(FormRouteEnum.MANUAL_VERIFY);
          return;
        }

        throw new Error(createResult.error);
      }

      switch (createResult.status) {
        case 'accepted':
        case 'accepted_tier2':
          if (flow === 'M1') {
            setContactResult('accepted');
            navigateToRoute(FormRouteEnum.LOAN_AMOUNT);
          } else {
            handleApplicationSuccess();
          }
          break;

        case 'rejected':
          await handleRejection(flow);
          break;

        case 'ERROR':
          throw new Error(createResult.error || 'Verification failed');
      }
    } catch (err) {
      setSmsAttempts((prev) => prev + 1);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleResendCode = async () => {
    // Update marketing params with form data
    marketingParams.updateFormData(formData);

    setLoading(true);
    setLoadingMessage('Sending new code...');
    setError(null);

    try {
      const ipAddress = storageController.getUserIp() || '';
      const result = await formController.connectBySms(
        formData.phone,
        formData.email,
        formData.birthDate,
        formData.loanAmount,
        formData.loanPurpose,
        formData.employmentStatus,
        formData.employmentFrequency,
        formData.educationLevel,
        formData.annualIncome,
        formData.propertyStatus,
        ipAddress,
        marketingParams.getParams()
      );

      if (result.success) {
        setFormData((prev) => ({ ...prev, smsCode: '' }));
      } else {
        throw new Error(result.error || 'Failed to send new code');
      }
    } catch (err) {
      setSmsAttempts((prev) => prev + 1);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleReenterInfo = () => {
    setFormData((prev) => ({ ...prev, smsCode: '' }));
    navigateToRoute(FormRouteEnum.BIRTH_DATE);
  };

  const handleUpdatePersonalInfo = () => {
    navigateToRoute(FormRouteEnum.KBA_PERSONAL_INFO);
  };

  const handleUpdateBirthDate = () => {
    navigateToRoute(FormRouteEnum.BIRTH_DATE);
  };

  const handleEditReviewField = (field: ReviewEditableField) => {
    navigateToRoute(reviewEditController.startField(field));
  };

  const handleEditAllReviewDetails = () => {
    navigateToRoute(reviewEditController.startAll());
  };

  const handleResetAttempts = () => {
    setSmsAttempts(0);
    setProspectAttempts(0);
    setKbaAttempts(0);
    setError(null);
  };

  const handleChooseSms = () => {
    setError(null);
    navigateToRoute(FormRouteEnum.CONTACT_INFO);
  };

  const handleChooseKba = () => {
    setError(null);
    navigateToRoute(FormRouteEnum.KBA_PERSONAL_INFO);
  };

  const handleChooseProspect = () => {
    setError(null);
    setManualVerificationReason('sms_failure');
    navigateToRoute(FormRouteEnum.MANUAL_VERIFY);
  };

  const verifyWithExperianAndFallback = async (
    data: Partial<FormData>
  ): Promise<{ success: boolean; status?: string }> => {
    const experianResult = await formController.fallbackToExperian(data);

    if (experianResult.success && experianResult.qualified) {
      handleApplicationSuccess();
      return { success: true, status: 'accepted' };
    }

    if (!experianResult.success && shouldFallback(experianResult.code)) {
      const manualResult = await formController.createManualLead(data);

      if (manualResult.success && manualResult.qualified) {
        handleApplicationSuccess();
        return { success: true, status: 'accepted' };
      }

      if (!manualResult.success && manualResult.code === 400) {
        throw new Error('Verification failed. Please check your details and try again.');
      }

      handleApplicationFailure();
      return { success: true, status: 'rejected' };
    }

    handleApplicationFailure();
    return { success: true, status: 'rejected' };
  };

  const handleManualVerification = async (
    data: Partial<FormData>
  ): Promise<{ success: boolean; status?: string; error?: string }> => {
    setLoading(true);
    setLoadingMessage('Verifying your information...');
    setError(null);

    const mergedData = { ...formData, ...data };
    setFormData(mergedData);

    try {
      if (manualVerificationReason === 'sms_failure') {
        const prospectResult = await formController.prospectVerify(mergedData);

        if (!prospectResult.success) {
          setProspectAttempts((prev) => prev + 1);
          const errorMsg = 'We were unable to verify your identity. Please try a different method.';
          setError(errorMsg);
          return { success: false, error: errorMsg };
        }
      }

      return await verifyWithExperianAndFallback(mergedData);
    } catch (err) {
      setProspectAttempts((prev) => prev + 1);
      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleKbaPersonalInfoSubmit = (data: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
    navigateToRoute(FormRouteEnum.KBA_SSN);
  };

  const handleKbaSsnSubmit = async (data: Partial<FormData>) => {
    const updatedFormData = { ...formData, ...data };
    setFormData(updatedFormData);
    setLoading(true);
    setLoadingMessage('Verifying your identity...');
    setError(null);

    try {
      const ipAddress = storageController.getUserIp() || '';
      const result = await formController.connectKba(
        updatedFormData,
        ipAddress,
        marketingParams.getParams()
      );

      if (!result.success) {
        throw new Error(result.error || 'Failed to start identity verification');
      }

      if (!result.questions || result.questions.length === 0) {
        throw new Error('No security questions received');
      }

      setKbaAttemptCount(1);
      
      setKbaQuestions(result.questions);
      navigateToRoute(FormRouteEnum.KBA_QUESTIONS);
    } catch (err) {
      setKbaAttempts((prev) => prev + 1);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleKbaQuestionsSubmit = async (answers: Record<string, string>) => {
    setLoading(true);
    setLoadingMessage('Submitting your answers...');
    setError(null);

    try {
      const verifyResult = await formController.verifyKba(answers, formData.email);

      if (!verifyResult.success) {
        if (verifyResult.code === 429) {
          setKbaAttempts(MAX_ATTEMPTS_PER_METHOD);
          return;
        }
        throw new Error(verifyResult.error || 'Identity verification failed');
      }

      if (
        verifyResult.connectionStatus === 'IN_PROGRESS' &&
        verifyResult.questions &&
        verifyResult.questions.length > 0
      ) {
        const nextKbaAttempts = kbaAttempts + 1;
        setKbaAttempts(nextKbaAttempts);

        if (nextKbaAttempts >= MAX_ATTEMPTS_PER_METHOD) {
          setError('We were unable to verify your identity. Please try a different method.');
          return;
        }

        const nextAttempt = kbaAttemptCount + 1;
        if (nextAttempt > MAX_KBA_ATTEMPTS) {
          setError('We were unable to verify your identity. Please try a different method.');
          return;
        }

        setKbaAttemptCount(nextAttempt);
        setKbaQuestions(verifyResult.questions);
        return;
      }

      setLoadingMessage('Checking eligibility...');
      const createResult = await formController.createContact(formData);

      if (!createResult.success) {
        if (createResult.status === 'ERROR' && shouldFallback(createResult.code)) {
          await verifyWithExperianAndFallback(formData);
          return;
        }
        throw new Error(createResult.error);
      }

      switch (createResult.status) {
        case 'accepted':
        case 'accepted_tier2':
          handleApplicationSuccess();
          break;
        case 'rejected':
          await handleRejection(flow);
          break;
        default:
          navigateToRoute(FormRouteEnum.UNSUCCESSFUL);
      }
    } catch (err) {
      setKbaAttempts((prev) => prev + 1);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleFormStep = (data: Partial<FormData>) => {
    if (currentStep === 0) {
      storageController.setApplicationStarted();
    }

    setFormData((prev) => {
      
      const updatedData = { ...prev, ...data };
      
      // Fix: Use UPDATED data, not stale formData
      marketingParams.updateFormData(updatedData);
      
      return updatedData;
    });

    if (reviewEditController.isActive()) {
      formDefaultsService.markStepSkipped(currentPath);
      navigateToRoute(reviewEditController.advance());
      return;
    }

    if (data.employmentStatus) {
      if (SKIP_FREQUENCY_STATUSES.includes(data.employmentStatus)) {
        setFormData((prev) => ({ ...prev, employmentFrequency: 'monthly' }));
        navigateToRoute(FormRouteEnum.ANNUAL_INCOME);
        return;
      }
    }

    const nextRoute = routeManager.getNextRoute(currentStep);

    if (flow === 'M1' && nextRoute === FormRouteEnum.VERIFY_SSN) {
      if (contactResult === 'accepted') {
        handleApplicationSuccess();
      } else {
        navigateToRoute(FormRouteEnum.VERIFY_SSN);
      }
      return;
    }

    if (nextRoute) {
      navigateToRoute(nextRoute);
    }
  };

  const submitOffers = async (email: string) => {
    setLoading(true);
    setLoadingMessage('Submitting application...');
    setError(null);

    try {
      const ipAddress = storageController.getUserIp() || '';
      const result = await formController.submitApplication({ ...formData, email }, ipAddress);

      if (result.success && result.offerId && result.storeUuid) {
        await handleApplicationOffers(result.offerId, result.storeUuid);
      } else {
        handleApplicationFailure();
      }
    } catch (err) {
      handleApplicationFailure();
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleOffersSubmit = async () => {
    if (!formData.email) {
      setShowEmailPrompt(true);
      return;
    }
    await submitOffers(formData.email);
  };

  const handleEmailVerified = async (email: string) => {
    setFormData(prev => ({ ...prev, email }));
    setShowEmailPrompt(false);
    await submitOffers(email);
  };

  const handleBack = () => {
    setError(null);
    const prevRoute = routeManager.getPreviousRoute(currentStep);
    if (prevRoute) {
      navigateToRoute(prevRoute);
    }
  };

  const isRedirectingToAcceptedLead = Boolean(
    leadDetails &&
    isAcceptedApplicationStatus(leadDetails.applicationStatus) &&
    !hasHandledLeadRedirect &&
    currentPath !== FormRouteEnum.SUCCESS
  );

  const smsExhausted = isSmsExhausted(smsAttempts);
  const prospectExhausted = isProspectExhausted(prospectAttempts);
  const kbaExhausted = isKbaExhausted(kbaAttempts);

  const smsAlternatives: AlternativeMethodCallbacks = {
    onChooseKba: !kbaExhausted ? handleChooseKba : undefined,
    onChooseProspect: !prospectExhausted ? handleChooseProspect : undefined,
  };

  const kbaAlternatives: AlternativeMethodCallbacks = {
    onChooseSms: !smsExhausted ? handleChooseSms : undefined,
    onChooseProspect: !prospectExhausted ? handleChooseProspect : undefined,
  };

  const prospectAlternatives: AlternativeMethodCallbacks = {
    onChooseSms: !smsExhausted ? handleChooseSms : undefined,
    onChooseKba: !kbaExhausted ? handleChooseKba : undefined,
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {echelonStage && <EchelonProgressOverlay activeStage={echelonStage} />}
      {loading && !echelonStage && <LoadingOverlay message={loadingMessage} />}
      {isPartnerLoading && <LoadingOverlay message="Loading your personalised details..." />}
      {(isLeadPreloadLoading || isRedirectingToAcceptedLead) && (
        <LoadingOverlay message="Loading your application..." />
      )}

      {showEmailPrompt && (
        <EmailVerificationModal
          onConfirm={handleEmailVerified}
          loading={loading}
        />
      )}

      <ProgressBar
        currentStep={progressStep}
        totalSteps={
          flow === 'N1' && !isUnqualified
            ? routeManager.getQualifiedFlowTotalSteps()
            : routeManager.getTotalSteps()
        }
        onBack={!ROUTES_WITHOUT_BACK.has(currentPath) ? handleBack : undefined}
      />

      <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-gray-100">
        <FormHeader />

        <div className="p-6">
          {currentPath === FormRouteEnum.START && (
            showPartnerReview ? (
              <ApplicationReviewForm
                formData={formData}
                onContinue={() => handleFormStep({})}
                onEditField={handleEditReviewField}
                onEditAllDetails={handleEditAllReviewDetails}
              />
            ) : (
              <StartForm onSubmit={handleFormStep} />
            )
          )}

          {currentPath === FormRouteEnum.EMAIL && (
            <EmailForm
              formData={formData}
              onChange={handleChange}
              onSuggestionAccept={(email) => {
                setFormData((prev) => ({ ...prev, email }));
                setValidationErrors((prev) => ({
                  ...prev,
                  email: validateEmail(email),
                }));
              }}
              onSubmit={handleFormStep}
            />
          )}

          {currentPath === FormRouteEnum.BIRTH_DATE && (
            <BirthDateForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.CONTACT_INFO && (
            <ContactInfoForm
              formData={formData}
              validationErrors={validationErrors}
              loading={loading}
              error={error}
              onChange={handleChange}
              onSubmit={handleConnectBySms}
            />
          )}

          {currentPath === FormRouteEnum.VERIFY_PHONE && (
            <PhoneVerificationForm
              formData={formData}
              loading={loading}
              error={error}
              onChange={handleChange}
              onSubmit={handleCodeVerification}
              onResendCode={handleResendCode}
              onReenterInfo={handleReenterInfo}
              smsAttempts={smsAttempts}
              methodExhausted={smsExhausted}
              {...smsAlternatives}
            />
          )}

          {currentPath === FormRouteEnum.LOAN_AMOUNT && (
            <LoanAmountForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.LOAN_PURPOSE && (
            <LoanPurposeForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.PROPERTY_STATUS && (
            <PropertyStatusForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.EMPLOYMENT_STATUS && (
            <EmploymentStatusForm
              formData={formData}
              onSubmit={handleFormStep}
            />
          )}

          {currentPath === FormRouteEnum.EMPLOYMENT_FREQUENCY && (
            <EmploymentFrequencyForm
              formData={formData}
              onSubmit={handleFormStep}
            />
          )}

          {currentPath === FormRouteEnum.ANNUAL_INCOME && (
            <AnnualIncomeForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.EDUCATION && (
            <EducationForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentPath === FormRouteEnum.VERIFY_SSN && (
            <SSNVerificationForm
              formData={formData}
              loading={loading}
              error={error}
              onChange={handleChange}
              onSubmit={handleOffersSubmit}
            />
          )}

          {currentPath === FormRouteEnum.SUCCESS && (
            <SuccessStep
              contactCenterAvailability={contactCenterAvailability}
              shouldSendCall={shouldSendCall}
              onScheduleAppointment={handleScheduleAppointment}
              onInitiateCall={handleInitiateCall}
              isBpoAgent={isBpoAgent}
              onVariantShown={handleVariantShown}
            />
          )}

          {currentPath === FormRouteEnum.UNSUCCESSFUL && (
            <UnsuccessfulApplication />
          )}

          {currentPath === FormRouteEnum.BLOCK && (
            <ApplicationBlocked onInitiateCall={handleInitiateCall} onStartNew={handleResetAttempts} />
          )}

          {currentPath === FormRouteEnum.MANUAL_VERIFY && (
            <ManualVerificationForm
              formData={formData}
              loading={loading}
              error={error}
              onSubmit={handleManualVerification}
              onBack={() => navigateToRoute(FormRouteEnum.VERIFY_PHONE)}
              onChange={(data) => setFormData((prev) => ({ ...prev, ...data }))}
              showOfferCode={manualVerificationReason === 'sms_failure'}
              methodExhausted={prospectExhausted}
              smsExhausted={smsExhausted}
              {...prospectAlternatives}
            />
          )}

          {currentPath === FormRouteEnum.KBA_PERSONAL_INFO && (
            <KbaPersonalInfoForm
              formData={formData}
              loading={loading}
              error={error}
              onSubmit={handleKbaPersonalInfoSubmit}
              methodExhausted={kbaExhausted}
              {...kbaAlternatives}
            />
          )}

          {currentPath === FormRouteEnum.KBA_SSN && (
            <KbaSsnForm
              formData={formData}
              loading={loading}
              error={error}
              onSubmit={handleKbaSsnSubmit}
              methodExhausted={kbaExhausted}
              onUpdatePersonalInfo={handleUpdatePersonalInfo}
              onUpdateBirthDate={handleUpdateBirthDate}
              {...kbaAlternatives}
            />
          )}

          {currentPath === FormRouteEnum.KBA_QUESTIONS && (
            <KbaQuestionsForm
              questions={kbaQuestions}
              loading={loading}
              error={error}
              onSubmit={handleKbaQuestionsSubmit}
              methodExhausted={kbaExhausted}
              {...kbaAlternatives}
            />
          )}
        </div>
      </div>

      {currentPath === FormRouteEnum.START && (
        <>
          <div className="mt-8 text-center">
            <TrustpilotWidget size="large" />
          </div>
        </>
      )}

      <SecurityTiles />

      <DeveloperTools formData={formData} />
    </div>
  );
}